/* Pointer-driven 3D tilt — generic, data-attribute driven.
   Loaded as a deferred asset so it runs reliably on the storefront.

   Contract:
     [data-pointer-tilt]  → a root that contains tilt items
       [data-tilt]        → each item (its rect defines the tilt origin)
         [data-tilt-face] → the element that receives the transform + CSS vars
   The face element (and anything inheriting from it) reads:
     --rx --ry --lift --glare --mx --my
   The root gets an `is-tilting` class while the loop is running. */
(function () {
  if (window.__pointerTilt) {
    window.__pointerTilt.scan();
    return;
  }

  var SELECTOR = '[data-pointer-tilt]';
  var MAX_TILT = 9; // degrees
  var LIFT = 16; // px translateZ on the nearest face
  var GLARE = 0.5; // peak sheen opacity

  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  var finePointer = window.matchMedia('(hover: hover) and (pointer: fine)');
  var instances = [];

  function clamp(v, a, b) {
    return v < a ? a : v > b ? b : v;
  }

  // Frame-rate independent smoothing factor
  function ease(base, dt) {
    return 1 - Math.pow(1 - base, dt * 60);
  }

  function Tilt(root) {
    this.root = root;
    this.items = [];
    this.px = 0;
    this.py = 0;
    this.intensity = 0;
    this.target = 0;
    this.falloff = 1;
    this.raf = 0;
    this.last = 0;
    this.dirty = true; // item rects need (re-)measuring before next frame

    this.frame = this.frame.bind(this);
    this.onMove = this.onMove.bind(this);
    this.onEnter = this.onEnter.bind(this);
    this.onLeave = this.onLeave.bind(this);

    this.collect();
    root.addEventListener('pointermove', this.onMove, { passive: true });
    root.addEventListener('pointerenter', this.onEnter, { passive: true });
    root.addEventListener('pointerleave', this.onLeave, { passive: true });
    root.__pointerTiltBound = this;
    instances.push(this);
  }

  Tilt.prototype.collect = function () {
    var nodes = this.root.querySelectorAll('[data-tilt]');
    this.items = [];
    for (var i = 0; i < nodes.length; i++) {
      var face = nodes[i].querySelector('[data-tilt-face]');
      if (!face) continue;
      this.items.push({
        box: nodes[i],
        el: face,
        rx: 0, ry: 0, lift: 0, glare: 0,
        cx: 0, cy: 0, hw: 1, hh: 1
      });
    }
  };

  // Reads only — kept separate from writes to avoid layout thrash
  Tilt.prototype.measure = function () {
    for (var i = 0; i < this.items.length; i++) {
      var c = this.items[i];
      var r = c.box.getBoundingClientRect();
      c.cx = r.left + r.width / 2;
      c.cy = r.top + r.height / 2;
      c.hw = r.width / 2 || 1;
      c.hh = r.height / 2 || 1;
    }
    var rr = this.root.getBoundingClientRect();
    this.falloff = Math.max(rr.width * 0.32, 1);
  };

  Tilt.prototype.onEnter = function (e) {
    this.px = e.clientX;
    this.py = e.clientY;
    this.target = 1;
    this.dirty = true;
    this.start();
  };

  Tilt.prototype.onMove = function (e) {
    this.px = e.clientX;
    this.py = e.clientY;
    this.target = 1;
    this.start();
  };

  Tilt.prototype.onLeave = function () {
    this.target = 0;
    this.start();
  };

  Tilt.prototype.start = function () {
    this.root.classList.add('is-tilting');
    if (this.raf) return;
    this.last = 0;
    this.raf = requestAnimationFrame(this.frame);
  };

  Tilt.prototype.frame = function (t) {
    if (!this.last) this.last = t;
    var dt = clamp((t - this.last) / 1000, 0, 0.05);
    this.last = t;

    // Item rects only change on enter / scroll / resize — re-measure then, not every frame
    if (this.dirty) {
      this.measure();
      this.dirty = false;
    }

    var ki = ease(0.16, dt);
    this.intensity += (this.target - this.intensity) * ki;

    var kc = ease(0.18, dt);
    var R2 = this.falloff * this.falloff;
    var settled = Math.abs(this.target - this.intensity) < 0.001;

    for (var i = 0; i < this.items.length; i++) {
      var c = this.items[i];
      var dx = this.px - c.cx;
      var dy = this.py - c.cy;
      var fall = R2 / (R2 + dx * dx + dy * dy); // 1 near pointer, → 0 far
      var amp = fall * this.intensity;

      var relX = clamp(dx / c.hw, -1.2, 1.2);
      var relY = clamp(dy / c.hh, -1.2, 1.2);

      var trx = -relY * MAX_TILT * amp;
      var tryv = relX * MAX_TILT * amp;
      var tlift = LIFT * amp;
      var tglare = clamp(fall, 0, 1) * this.intensity * GLARE;
      var tmx = (relX * 0.5 + 0.5) * 100;
      var tmy = (relY * 0.5 + 0.5) * 100;

      if (
        Math.abs(trx - c.rx) > 0.01 ||
        Math.abs(tryv - c.ry) > 0.01 ||
        Math.abs(tlift - c.lift) > 0.01 ||
        Math.abs(tglare - c.glare) > 0.004
      ) {
        settled = false;
      }

      c.rx += (trx - c.rx) * kc;
      c.ry += (tryv - c.ry) * kc;
      c.lift += (tlift - c.lift) * kc;
      c.glare += (tglare - c.glare) * kc;

      var s = c.el.style;
      s.setProperty('--rx', c.rx.toFixed(3) + 'deg');
      s.setProperty('--ry', c.ry.toFixed(3) + 'deg');
      s.setProperty('--lift', c.lift.toFixed(2) + 'px');
      s.setProperty('--glare', c.glare.toFixed(3));
      s.setProperty('--mx', tmx.toFixed(1) + '%');
      s.setProperty('--my', tmy.toFixed(1) + '%');
    }

    // Settle-and-sleep: stop the loop once everything is at rest
    if (settled) {
      this.raf = 0;
      this.root.classList.remove('is-tilting');
    } else {
      this.raf = requestAnimationFrame(this.frame);
    }
  };

  Tilt.prototype.destroy = function () {
    if (this.raf) cancelAnimationFrame(this.raf);
    this.raf = 0;
    this.root.classList.remove('is-tilting');
    this.root.removeEventListener('pointermove', this.onMove);
    this.root.removeEventListener('pointerenter', this.onEnter);
    this.root.removeEventListener('pointerleave', this.onLeave);
    this.root.__pointerTiltBound = null;
    var idx = instances.indexOf(this);
    if (idx > -1) instances.splice(idx, 1);
  };

  function scan() {
    if (reduceMotion.matches || !finePointer.matches) return;
    var roots = document.querySelectorAll(SELECTOR);
    for (var i = 0; i < roots.length; i++) {
      if (!roots[i].__pointerTiltBound) new Tilt(roots[i]);
    }
  }

  window.__pointerTilt = { scan: scan };

  if (document.readyState !== 'loading') scan();
  else document.addEventListener('DOMContentLoaded', scan);

  // Re-measure engaged instances when the page reflows under them
  window.addEventListener('scroll', function () {
    for (var i = 0; i < instances.length; i++) {
      if (instances[i].target === 1) {
        instances[i].dirty = true;
        instances[i].start();
      }
    }
  }, { passive: true });
  window.addEventListener('resize', function () {
    for (var i = 0; i < instances.length; i++) {
      instances[i].dirty = true;
      instances[i].start();
    }
  }, { passive: true });

  // Theme editor lifecycle
  document.addEventListener('shopify:section:load', scan);
  document.addEventListener('shopify:section:unload', function (e) {
    var root = e.target.querySelector(SELECTOR);
    if (root && root.__pointerTiltBound) root.__pointerTiltBound.destroy();
  });
})();
